import { useCallback, useMemo, useState } from "react";
import type { DeveloperFeatureFlagSettings } from "@/v2/apis/developerFeatureFlags/developer-feature-flags-apis";
import {
  useDeveloperFeatureFlags,
  useUpsertDeveloperFeatureFlags,
} from "@/v2/queries/developerFeatureFlags";

/**
 * Feature flags persisted per-organization in the backend.
 * Super admins toggle these for any whitelabel via the Feature Flags page.
 * The settings apply to all dashboard users of that whitelabel.
 */
export interface FeatureFlags {
  supersetAnalytics: boolean;
  aiAnalytics: boolean;
  legacyAnalytics: boolean;
  partnerAccess: boolean;
  fraudPrevention: boolean;
  vehicleModels: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  supersetAnalytics: false,
  aiAnalytics: false,
  legacyAnalytics: true,
  partnerAccess: false,
  fraudPrevention: false,
  vehicleModels: false,
};

function toFeatureFlags(
  settings: DeveloperFeatureFlagSettings | undefined
): FeatureFlags {
  if (!settings) return DEFAULT_FLAGS;
  return {
    supersetAnalytics: settings.superset_analytics ?? false,
    aiAnalytics: settings.ai_analytics ?? false,
    legacyAnalytics: settings.legacy_analytics ?? true,
    partnerAccess: settings.partner_access ?? false,
    fraudPrevention: settings.fraud_prevention ?? false,
    vehicleModels: settings.vehicle_models ?? false,
  };
}

/**
 * Only the flags this editor owns. The backend merges rather than replaces, so
 * settings it does not name — `omni_end_lock_verification`, the onboarding
 * deposit cutover — survive a save untouched.
 */
function toSettings(flags: FeatureFlags): DeveloperFeatureFlagSettings {
  return {
    superset_analytics: flags.supersetAnalytics,
    ai_analytics: flags.aiAnalytics,
    legacy_analytics: flags.legacyAnalytics,
    partner_access: flags.partnerAccess,
    fraud_prevention: flags.fraudPrevention,
    vehicle_models: flags.vehicleModels,
  };
}

const FLAG_KEYS = Object.keys(DEFAULT_FLAGS) as (keyof FeatureFlags)[];

/**
 * Hook for reading feature flags from the backend.
 * Used by DashboardScreen to gate routes and sidebar items.
 */
export function useFeatureFlags(organizationId: string | null): FeatureFlags {
  const { data } = useDeveloperFeatureFlags(organizationId);
  return useMemo(() => toFeatureFlags(data), [data]);
}

/**
 * Hook for the Feature Flags settings page.
 * Returns local draft state with dirty tracking and explicit save/discard.
 */
export function useFeatureFlagsEditor(organizationId: string | null) {
  const { data, isLoading } = useDeveloperFeatureFlags(organizationId);
  const upsertMutation = useUpsertDeveloperFeatureFlags();

  const serverFlags = useMemo(() => toFeatureFlags(data), [data]);

  // The draft carries the organization it was typed against, so switching
  // whitelabel discards it by derivation rather than by an effect firing after
  // the new organization's flags have already rendered against the old draft.
  const [draft, setDraft] = useState<{
    organizationId: string | null;
    flags: FeatureFlags;
  } | null>(null);
  const activeDraft =
    draft && draft.organizationId === organizationId ? draft.flags : null;

  const flags = activeDraft ?? serverFlags;
  const isDirty =
    activeDraft != null &&
    FLAG_KEYS.some((key) => activeDraft[key] !== serverFlags[key]);

  const setFlag = useCallback(
    (flag: keyof FeatureFlags, value: boolean) => {
      setDraft((prev) => ({
        organizationId,
        flags: {
          ...(prev && prev.organizationId === organizationId
            ? prev.flags
            : serverFlags),
          [flag]: value,
        },
      }));
    },
    [organizationId, serverFlags]
  );

  const save = useCallback(
    (callbacks?: { onSuccess?: () => void; onError?: () => void }) => {
      if (!organizationId || !isDirty) return;
      upsertMutation.mutate(
        { orgId: organizationId, settings: toSettings(flags) },
        {
          onSuccess: () => {
            setDraft(null);
            callbacks?.onSuccess?.();
          },
          onError: callbacks?.onError,
        }
      );
    },
    [organizationId, isDirty, flags, upsertMutation]
  );

  const discard = useCallback(() => {
    setDraft(null);
  }, []);

  return {
    flags,
    setFlag,
    save,
    discard,
    isDirty,
    isLoading,
    isSaving: upsertMutation.isPending,
  };
}
